import { canUseStrokeOfLuck, strokeOfLuckDecision } from '../../../shared/rules/rogue.mjs';
import { getIndomitableProfile } from '../../../shared/rules/fighter.mjs';
import { hasHeroicInspiration } from '../../../shared/rules/heroicInspiration.mjs';
import { canUseFate } from '../../../shared/rules/epicBoons.mjs';
import { focusProfile, hasMonkFeature } from '../../../shared/rules/monk.mjs';
import { bardicDieLabel, getBardicInspirationProfile, heldBardicDie } from '../../../shared/rules/bard.mjs';
import { canUsePeerlessSkill, peerlessSkillLabel } from '../../../shared/rules/subclasses/bard/lore.mjs';
import { calculateAbilityModifier } from './derivedStats';
import { askCountercharm } from './countercharmService';

// One presenter (StrokeOfLuckPrompt, mounted on the sheet) shows the question for any roll: saves rolled in prompts,
// death saves and ability checks. Without one the answer is "keep the roll".
let presenter = null;
export const registerStrokeOfLuckPresenter = (show) => { presenter = show; return () => { if (presenter === show) presenter = null; }; };

/**
 * After a failed D20 Test: asks whether to turn it into a 20 (Stroke of Luck, Rogue 20) and resolves true when the
 * player said yes. `id` names the roll; `what` and `outcome` word the question.
 */
export const offerStrokeOfLuck = async (character, { failed = true, id, what, outcome }) => {
  if (!failed || !canUseStrokeOfLuck(character) || !presenter) return false;
  const choice = await presenter(strokeOfLuckDecision(id, what, outcome));
  return choice?.choiceId === 'use';
};

/**
 * After a failed saving throw: offers every rescue the creature has, Stroke of Luck (Rogue 20, the d20 becomes a 20),
 * Indomitable (Fighter 9, reroll with the Fighter level added), Heroic Inspiration (reroll) and a held Bardic Inspiration
 * die (added to the roll); the server rolls a reroll or the die. Resolves the request fields to send:
 * `{ strokeOfLuck: true }`, `{ indomitable: true }`, `{ heroicInspiration: true }`, `{ bardicInspiration: true }` or `{}`
 * to keep the roll. `save: false` (an ability check) offers everything but Indomitable, plus Peerless Skill
 * (`{ peerlessSkill: true }`).
 */
const ownSaveRescue = async (character, { failed = true, id, what, outcome, save = true }) => {
  if (!failed || !presenter) return {};
  const stroke = canUseStrokeOfLuck(character);
  const indomitable = save ? getIndomitableProfile(character) : null;
  const heroic = hasHeroicInspiration(character);
  const fate = canUseFate(character);
  const inspired = heldBardicDie(character);
  const charisma = calculateAbilityModifier(character, 'cha');
  const peerless = !save && canUsePeerlessSkill(character, charisma) ? getBardicInspirationProfile(character, charisma).sides : 0;
  // Disciplined Survivor (Monk 14): reroll a failed saving throw for 1 Focus Point.
  const disciplined = save && hasMonkFeature(character, 'disciplinedSurvivor') && focusProfile(character).remaining >= 1;
  if (!indomitable?.remaining && !heroic && !fate && !disciplined && !inspired && !peerless) return stroke && await offerStrokeOfLuck(character, { failed, id, what, outcome }) ? { strokeOfLuck: true } : {};
  const choice = await presenter({ id: `${id}:save-rescue`, featureId: 'save-rescue', title: save ? 'Failed saving throw' : 'Failed ability check',
    prompt: [`Your ${what} failed.`, stroke ? `Stroke of Luck turns the d20 into a 20${outcome ? ` (${outcome})` : ''}.` : '',
      indomitable?.remaining ? `Indomitable rerolls it with +${indomitable.level} (your Fighter level).` : '',
      inspired ? `Bardic Inspiration adds your d${inspired.sides} to it.` : '', peerless ? `Peerless Skill adds your d${peerless} to it, and keeps the use if it still fails.` : '', heroic ? 'Heroic Inspiration rerolls it.' : '', fate ? 'Boon of Fate adds 2d4 to it.' : '',
      disciplined ? 'Disciplined Survivor rerolls it for 1 Focus Point.' : '',
      indomitable?.remaining || heroic || disciplined ? 'A reroll must be used.' : ''].filter(Boolean).join(' '),
    choices: [...(stroke ? [{ id: 'stroke', label: 'Make it a 20', action: 'apply' }] : []),
      ...(indomitable?.remaining ? [{ id: 'indomitable', label: `Reroll +${indomitable.level} · Indomitable (${indomitable.remaining} left)`, action: 'apply' }] : []),
      ...(inspired ? [{ id: 'bardic', label: bardicDieLabel(inspired), action: 'apply' }] : []),
      ...(peerless ? [{ id: 'peerless', label: peerlessSkillLabel(peerless), action: 'apply' }] : []),
      ...(heroic ? [{ id: 'heroic', label: 'Reroll · Heroic Inspiration', action: 'apply' }] : []),
      ...(fate ? [{ id: 'fate', label: '+2d4 · Boon of Fate', action: 'apply' }] : []),
      ...(disciplined ? [{ id: 'disciplined', label: `Reroll · Disciplined Survivor (1 Focus, ${focusProfile(character).remaining} left)`, action: 'apply' }] : []),
      { id: 'skip', label: 'Keep the roll' }] });
  return choice?.choiceId === 'stroke' ? { strokeOfLuck: true } : choice?.choiceId === 'indomitable' ? { indomitable: true }
    : choice?.choiceId === 'heroic' ? { heroicInspiration: true } : choice?.choiceId === 'fate' ? { fate: true }
      : choice?.choiceId === 'disciplined' ? { disciplinedSurvivor: true }
        : choice?.choiceId === 'bardic' ? { bardicInspiration: true }
          : choice?.choiceId === 'peerless' ? { peerlessSkill: true } : {};
};

/** `ownSaveRescue`, then, when `countercharm` describes a save the player kept, a nearby Bard's Countercharm. */
export const offerSaveRescue = async (character, { countercharm, ...offer }) => {
  const own = await ownSaveRescue(character, offer);
  const failed = offer.failed !== false, save = offer.save !== false;
  return Object.keys(own).length || !countercharm || !failed || !save ? own : askCountercharm(countercharm);
};
